import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectorRef,
  OnChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiIconComponent,
} from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { EntityType } from 'src/app/utils/enums/self-serve/assignment-type.enum';

@Component({
  selector: 'delete-modal',
  standalone: true,
  templateUrl: './delete-modal.component.html',
  styleUrls: ['./delete-modal.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    QaiDialogComponent,
    QaiButtonComponent,
    QaiIconComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class DeleteModalComponent implements OnChanges {
  @ViewChild('deleteDialog') dialogRef: QaiDialogComponent | undefined;

  @Input() type: EntityType = EntityType.SIMULATION;
  @Input() open: boolean = false;
  @Input() networkRequestInProgress: boolean = false;
  @Input() assignmentStatus?: string;
  @Input() moduleUuid?: string;

  @Output() openChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();

  heading = '';
  content = '';
  deleteLabel = '';
  isDeleteDisabled = false;

  constructor(private cdRef: ChangeDetectorRef, private translocoService: TranslocoService) {}

  private getLabelsMap(): Record<EntityType, { heading: string; content: string; deleteLabel: string; isDeleteDisabled: boolean }> {
    return {
      [EntityType.SIMULATION]: {
        heading: this.translocoService.translate('simulationBuilder.DELETE_SIM_HEADING'),
        content: this.translocoService.translate('simulationBuilder.DELETE_SIM_CONTENT'),
        deleteLabel: this.translocoService.translate('simulationBuilder.DELETE_SIM_LABEL'),
        isDeleteDisabled: false,
      },
      [EntityType.ASSIGNMENT]: {
        heading: this.translocoService.translate('simulationBuilder.DELETE_ASSIGNMENT_HEADING'),
        content: this.translocoService.translate('simulationBuilder.DELETE_ASSIGNMENT_CONTENT'),
        deleteLabel: this.translocoService.translate('simulationBuilder.DELETE_ASSIGNMENT_LABEL'),
        isDeleteDisabled: false,
      },
      [EntityType.NON_STAGED_ASSIGNMENT]: {
        heading: this.translocoService.translate('simulationBuilder.DELETE_ASSIGNMENT_HEADING'),
        content: this.translocoService.translate('simulationBuilder.DELETE_ASSIGNMENT_NON_STAGED_CONTENT'),
        deleteLabel: this.translocoService.translate('simulationBuilder.DELETE_ASSIGNMENT_LABEL'),
        isDeleteDisabled: true,
      },
      [EntityType.USER_ASSIGNMENT]: {
        heading: this.translocoService.translate('simulationBuilder.REMOVE_FROM_ASSIGNMENT_HEADING'),
        content: this.translocoService.translate('simulationBuilder.REMOVE_FROM_ASSIGNMENT_CONTENT'),
        deleteLabel: this.translocoService.translate('simulationBuilder.REMOVE_FROM_ASSIGNMENT_LABEL'),
        isDeleteDisabled: false,
      },
    };
  }

  ngOnChanges(): void {
    // SAFE: fixed internal status literals
    const normalizedStatus = this.assignmentStatus?.toLowerCase() || '';
    const isDeletableStatus = normalizedStatus === 'staged' || normalizedStatus === 'draft';
    const labelKey =
      this.type === EntityType.ASSIGNMENT && !isDeletableStatus
        ? EntityType.NON_STAGED_ASSIGNMENT
        : this.type;

    const labels = this.getLabelsMap()[labelKey];
    this.heading = labels.heading;
    this.content = labels.content;
    this.deleteLabel = labels.deleteLabel;
    this.isDeleteDisabled = labels.isDeleteDisabled;

    this.updateModalState();
  }

  handleDelete(): void {
    this.confirm.emit();
  }

  handleClose(): void {
    this.setClosedState();
  }

  private updateModalState(): void {
    if (this.open) {
      this.dialogRef?.showModal();
    } else {
      this.dialogRef?.close();
    }
  }

  private setClosedState(): void {
    if (this.open) {
      this.closeModal.emit();
      this.open = false;
      this.dialogRef?.close();
      this.openChange.emit(false);
      this.cdRef.detectChanges();
    }
  }
}
